import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatEuro, formatMinutes, formatDateTime } from "@/lib/api";
import { ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Area, AreaChart } from "recharts";
import { FileText, CalendarCheck, TrendingUp, Clock, Wallet, AlertCircle, Zap, PhoneOff, Sparkles, ArrowRight } from "lucide-react";

function KPI({ label, value, sub, icon: Icon, accent = "#19C3B3", highlight = false, testid }) {
  return (
    <div className={`kf-card p-5 ${highlight ? "ring-2 ring-[#19C3B3]/50" : ""}`} data-testid={testid || `kpi-${label}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-2 font-display text-3xl font-extrabold text-[#16324F]">{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const [a, setA] = useState(null);
  const [recent, setRecent] = useState([]);
  const [facts, setFacts] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setA(r.data));
    api.get("/quotes").then((r) => setRecent(r.data.slice(0, 8)));
    api.get("/nenzio/facts").then((r) => setFacts(r.data)).catch(() => setFacts(null));
  }, []);

  if (!a) return <div className="text-slate-500">Chargement…</div>;

  const pr = facts?.priorites_resume;
  const attention = pr?.priorites_affichees ?? 0;
  const autres = pr?.autres_en_attente ?? 0;

  return (
    <div>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#16324F]">Tableau de bord</h1>
      <p className="mt-1 text-slate-500">L&apos;impact business de MonTempsGagné — <span className="italic">estimations basées sur vos paramètres</span>.</p>

      {/* Résumé Nenzio (déterministe, sans appel LLM) */}
      <Link
        to="/app/nenzio"
        className="mt-6 flex items-start gap-4 kf-card p-5 hover:shadow-md transition-base group"
        data-testid="nenzio-summary-card"
      >
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br from-[#16324F] to-[#19C3B3] text-white">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-[#16324F]">Nenzio</span>
            <span className="text-xs font-semibold text-[#19C3B3]">Votre copilote IA</span>
          </div>
          {!facts ? (
            <div className="text-sm text-slate-400 mt-1">Analyse de votre activité…</div>
          ) : attention === 0 ? (
            <div className="text-sm mt-1" data-testid="nenzio-summary-empty">
              <div className="text-[#16324F] font-medium">Tout est à jour pour aujourd&apos;hui.</div>
              {autres > 0 && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {autres} élément{autres > 1 ? "s" : ""} rest{autres > 1 ? "ent" : "e"} en attente, mais aucun ne nécessite d&apos;action immédiate.
                </div>
              )}
            </div>
          ) : (
            <div className="mt-1">
              <div className="text-sm text-[#16324F] font-medium" data-testid="nenzio-summary-count">
                {attention} élément{attention > 1 ? "s" : ""} mérite{attention > 1 ? "nt" : ""} votre attention aujourd&apos;hui.
              </div>
              <ul className="mt-1 space-y-0.5">
                {pr.devis_a_relancer > 0 && (
                  <li className="text-xs text-slate-500">• {pr.devis_a_relancer} devis à relancer</li>
                )}
                {pr.nouvelles_demandes > 0 && (
                  <li className="text-xs text-slate-500">• {pr.nouvelles_demandes} nouvelle{pr.nouvelles_demandes > 1 ? "s" : ""} demande{pr.nouvelles_demandes > 1 ? "s" : ""} à traiter</li>
                )}
                {pr.rendez_vous_aujourdhui > 0 && (
                  <li className="text-xs text-slate-500">• {pr.rendez_vous_aujourdhui} rendez-vous aujourd&apos;hui</li>
                )}
              </ul>
              {autres > 0 && (
                <div className="text-xs text-slate-400 mt-1" data-testid="nenzio-summary-backlog">
                  {autres} autre{autres > 1 ? "s" : ""} élément{autres > 1 ? "s" : ""} rest{autres > 1 ? "ent" : "e"} en attente.
                </div>
              )}
            </div>
          )}
          <div className="mt-2 text-xs font-semibold text-[#16324F] inline-flex items-center gap-1">
            Faire le point avec Nenzio
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-base" />
          </div>
        </div>
      </Link>

      {/* Primary KPI row */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          label="Temps de traitement des devis estimé économisé"
          value={formatMinutes(a.processing_time_saved_minutes)}
          sub={`Base : ${a.avg_quote_processing_min} min / devis`}
          icon={Zap} accent="#19C3B3" highlight
          testid="kpi-processing-time-saved"
        />
        <KPI label="Devis générés automatiquement" value={a.automated_count ?? (a.total_quotes - a.to_review)} sub={`Total : ${a.total_quotes}`} icon={FileText} accent="#16324F" testid="kpi-automated" />
        <KPI label="Taux d'automatisation" value={`${a.automation_rate}%`} sub="Devis traités sans intervention" icon={TrendingUp} accent="#FF7A59" testid="kpi-automation-rate" />
        <KPI label="Réservations sans intervention humaine" value={a.bookings_without_call} sub={`Cette semaine : ${a.bookings_week}`} icon={CalendarCheck} accent="#19C3B3" testid="kpi-bookings-nocall" />
      </div>

      {/* Secondary KPI row */}
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          label="Appels / rappels estimés évités"
          value={formatMinutes(a.time_saved_minutes)}
          sub={`Base : ${a.avg_call_duration_min} min / appel`}
          icon={PhoneOff} accent="#16324F"
          testid="kpi-calls-avoided"
        />
        <KPI label="Conversion devis → réservation" value={`${a.conversion_rate}%`} icon={TrendingUp} accent="#FF7A59" testid="kpi-conversion" />
        <KPI label="Montant des devis générés" value={formatEuro(a.total_value)} icon={Wallet} accent="#16324F" testid="kpi-total-value" />
        <KPI label="Demandes à étudier" value={a.to_review} sub="Nécessitent une vérification" icon={AlertCircle} accent="#FF7A59" testid="kpi-to-review" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 kf-card p-6">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-display font-bold text-lg text-[#16324F]">Volume de devis · 14 derniers jours</div>
              <div className="text-xs text-slate-500">Demandes reçues via MonTempsGagné</div>
            </div>
            <div className="text-xs text-slate-400">Aujourd&apos;hui : <span className="font-semibold text-[#16324F]">{a.quotes_today}</span></div>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={a.series}>
                <defs>
                  <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#19C3B3" stopOpacity={0.5}/>
                    <stop offset="100%" stopColor="#19C3B3" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EAF1F7" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5).replace("-", "/")} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={30}/>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAF1F7" }}/>
                <Area type="monotone" dataKey="quotes" stroke="#16324F" strokeWidth={2} fill="url(#cg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="kf-card p-6">
          <div className="font-display font-bold text-lg text-[#16324F]">Activité récente</div>
          <div className="mt-4 space-y-3">
            {recent.length === 0 && <div className="text-sm text-slate-400">Aucune demande pour le moment.</div>}
            {recent.map((q) => (
              <div key={q.id} className="flex items-start justify-between gap-3 text-sm">
                <div>
                  <div className="font-semibold text-[#16324F]">{q.vehicle?.brand} {q.vehicle?.model}</div>
                  <div className="text-xs text-slate-500">{q.service_name} · {formatDateTime(q.created_at)}</div>
                </div>
                <div className="font-semibold text-[#16324F]">{q.price ? formatEuro(q.price) : <span className="text-xs text-[#FF7A59]">À étudier</span>}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
